import * as React from 'react';

import { Flex } from '@opendoor/bricks/core';
import { IAddressInputAddress } from '@opendoor/cinderblocks/growth/AddressInput/AddressInput';
import { standardizeAddress } from '@opendoor/cinderblocks/helpers/api';
import { globalObservability } from '@opendoor/observability/slim';

import { unsubscribeMailer } from '../api';
import { GOOGLE_MAPS_API_KEY } from '../globals';
import { AddressEntryPage } from './AddressEntryPage';
import { ErrorPage } from './ErrorPage';
import { ManualAddressPage } from './ManualAddressPage';
import { SuccessPage } from './SuccessPage';
import { VerifyAddressPage } from './VerifyAddressPage';

const Sentry = globalObservability.getSentryClient();

export interface CasaAddress {
  addressUuid?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  postalCode?: string;
  state?: string;
  street?: string;
  unitNumber?: string;
  unitType?: string;
}

interface IState {
  casaAddress: CasaAddress | null;
  pageType: 'address-entry' | 'verify' | 'manual' | 'success' | 'error';
}

export class UnsubscribePage extends React.PureComponent<Record<string, never>, IState> {
  public state: IState = {
    casaAddress: null,
    pageType: 'address-entry',
  };

  public render() {
    return (
      <Flex justifyContent="center" my={12} px={[7, 10]}>
        {this.renderUnsubscribeSection()}
      </Flex>
    );
  }

  public renderUnsubscribeSection() {
    const { casaAddress, pageType } = this.state;

    switch (pageType) {
      case 'error':
        return <ErrorPage casaAddress={casaAddress} />;

      case 'success':
        return <SuccessPage casaAddress={casaAddress} />;

      case 'manual':
        return (
          <ManualAddressPage
            addressInput={casaAddress && this.casaAddressToAddressInput(casaAddress)}
            cancelManualAddressVerification={this.cancelManualAddressVerification}
            modifyAddress={this.submitAddress}
          />
        );

      case 'verify':
        if (casaAddress) {
          return (
            <VerifyAddressPage
              casaAddress={casaAddress}
              googleMapsApiKey={GOOGLE_MAPS_API_KEY}
              toManualAddressVerification={() => this.setState({ pageType: 'manual' })}
              unsubscribeAddress={() => this.unsubscribeAddress(casaAddress)}
            />
          );
        }

        this.setState({ pageType: 'error' });
        return;

      default:
        return (
          <AddressEntryPage
            onChooseManualEntry={() => this.setState({ pageType: 'manual' })}
            onSubmit={this.submitAddress}
          />
        );
    }
  }

  public submitAddress = (address: IAddressInputAddress) => {
    standardizeAddress(address).then((response) => {
      if (!response || !response.data || response.errors) {
        this.setState({ pageType: 'manual' });
      } else {
        this.setState({
          casaAddress: this.getCasaAddressData(response.data),
          pageType: 'verify',
        });
      }
    });
  };

  public unsubscribeAddress = (address: CasaAddress) => {
    if (address && address.addressUuid) {
      unsubscribeMailer(address.addressUuid).then((response) => {
        if (!response || !response.data || response.errors) {
          this.setState({ pageType: 'error' });
          Sentry.captureException?.('Unsubscribe mailer request failed', {
            captureContext: {
              extra: { error: response.message },
            },
          });
        } else {
          this.setState({ pageType: 'success' });
        }
      });
    } else {
      this.setState({ pageType: 'error' });
    }
  };

  public cancelManualAddressVerification = (hasAddressInput: boolean) => {
    this.setState({ pageType: hasAddressInput ? 'verify' : 'address-entry' });
  };

  private getCasaAddressData(data: any): CasaAddress {
    if (!data.casa || !data.casa.findByFields || !data.casa.findByFields.address) {
      return {};
    }

    const address = data.casa.findByFields.address;
    return {
      addressUuid: address.uuid,
      city: address.city,
      latitude: address.latitude,
      longitude: address.longitude,
      postalCode: address.postalCode,
      state: address.state,
      street: address.street,
      unitNumber: address.unitNumber,
      unitType: address.unitType,
    };
  }

  private casaAddressToAddressInput(address: CasaAddress): IAddressInputAddress {
    return {
      city: address.city || '',
      postal_code: address.postalCode || '',
      state: address.state || '',
      street1: address.street || '',
      unit: address.unitNumber || '',
    };
  }
}
